import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'
import uniq from 'lodash-es/uniq'

import type * as core from '../../core'

/**
 * Represents a specific {@link core.admin.Page} that was custom developed into
 * the codebase
 *
 * This is in contrast to a {@link core.mosaic.Page}, which can be built by
 * users with sufficient permissions, rather than having to be prebuilt into the
 * codebase
 */
export type Page = core.admin.Page & core.common.Identified<Page.Id>
export namespace Page {
  /**
   * Identifier for a {@link Page}
   */
  export enum Id {
    // DMS
    DmsCatalog = 'dms:catalog',
    DmsDimension = 'dms:dimension',
    DmsEnum = 'dms:enum',
    DmsEntity = 'dms:entity',
    DmsGraph = 'dms:graph',
    // Mosaic Dev
    MosaicDevPages = 'mosaic:dev:pages',
    MosaicDevWidgets = 'mosaic:dev:widgets',
    MosaicDevActions = 'mosaic:dev:actions',
    MosaicDevPageBuilder = 'mosaic:dev:page-builder',
    MosaicDevWidgetBuilder = 'mosaic:dev:widget-builder',
    // Quiver
    /** @deprecated - Moved to admin center */
    QuiverDeploymentCenter = 'quiver:deployment-center',
    // Stm
    StmChat = 'stm:chat',
    // Dolomite
    DolomiteChat = 'dolomite:chat',
    // Dolomite General
    DolomiteGeneralChat = 'dolomite-general:chat',
    // Summit
    SummitOrganization = 'summit:org',
    SummitAllocation = 'summit:allocation',
    SummitPulse = 'summit:pulse',
    SummitCheckIns = 'summit:check-ins',
    SummitArchive = 'summit:archive',
    // Rate
    RateRules = 'rate:rules',
    RateGl = 'rate:gl',
    RateAllocations = 'rate:allocs',
    RateReports = 'rate:reports',
    RateReport = 'rate:report',
    // Space
    SpaceRecordDetails = 'space:record-details',
    SpaceRecordsList = 'space:records-list',
    SpaceLiteRecordDetails = 'space-lite:record-details',
    SpaceLiteRecordsList = 'space-lite:records-list',
    SpaceClusters = 'space:clusters',
    SpaceCluster = 'space:cluster',
    SpaceReports = 'space:reports',
    SpaceReport = 'space:report',
    SpaceServiceCenters = 'space:service-centers',
    SpaceOrganizations = 'space:organizations',
    SpaceSurveyConfiguration = 'space:survey-configuration',
    SpaceLiteSurveyConfiguration = 'space-lite:survey-configuration',
    SpaceUserAccess = 'space:user-access',
    SpaceLiteUserAccess = 'space-lite:user-access',
  }
  // Needs to be before namespace is defined
  const IDS: ReadonlySet<Id> = new Set(Object.values(Id) as Id[])
  export namespace Id {
    export const ALL: readonly Id[] = Array.from(IDS)
    export const isId = TypeChecker.Factory.oneOf(IDS)
    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)
  }

  /**
   * Contains information about all of the {@link Page}
   *
   * Useful when presenting information to an administrator that is selecting
   * the pages for an app
   */
  const LOOKUP: Record<Id, Omit<Page, 'id'>> = {
    [Id.DmsCatalog]: {
      description: 'Explore the catalog of models in the current DMS schema',
      name: 'DMS - Catalog',
    },
    [Id.DmsGraph]: {
      description: 'Explore a graph representation of the current DMS schema',
      name: 'DMS - Graph',
    },
    [Id.DmsDimension]: {
      description: 'Everything you want to see on a specific DMS dimension',
      name: 'DMS - Dimension',
    },
    [Id.DmsEnum]: {
      description: 'View details and entries of a DMS enum',
      name: 'DMS - Enum',
    },
    [Id.DmsEntity]: {
      description: 'Everything you want to see on a specific DMS entity',
      name: 'DMS - Entity',
    },
    [Id.MosaicDevPages]: {
      description: 'Develop Mosaic pages',
      name: 'Mosaic (Developer) - Pages',
    },
    [Id.MosaicDevPageBuilder]: {
      description: 'Develop Mosaic pages using page builder',
      name: 'Mosaic (Developer) - Page Builder',
    },
    [Id.MosaicDevWidgetBuilder]: {
      description: 'Develop Mosaic widgets using widget builder',
      name: 'Mosaic (Developer) - Widget Builder',
    },
    [Id.MosaicDevWidgets]: {
      description: 'Develop Mosaic widgets',
      name: 'Mosaic (Developer) - Widgets',
    },
    [Id.MosaicDevActions]: {
      description: 'Develop Mosaic actions',
      name: 'Mosaic (Developer) - Actions',
    },
    [Id.QuiverDeploymentCenter]: {
      description:
        'Deploy a variety of resources across the site through Quiver',
      name: 'Quiver - Deployment Center',
    },
    [Id.RateAllocations]: {
      description: 'Shows the breakdown of allocations in a Rate proposal',
      name: 'Rate - Allocations Summary',
    },
    [Id.RateGl]: {
      description: 'Presents and allows modifications to the mapped GL',
      name: 'Rate - GL List',
    },
    [Id.RateReports]: {
      description: 'Presents all of the reports in a Rate proposal',
      name: 'Rate - Reports List',
    },
    [Id.RateReport]: {
      description: 'TODO',
      name: 'Rate - Report Details',
    },
    [Id.RateRules]: {
      description: 'Manages rules used to map a GL',
      name: 'Rate - Rules List',
    },
    [Id.SpaceClusters]: {
      description: 'Manages clusters used to functionalize space',
      name: 'Space - Clusters List',
    },
    [Id.SpaceCluster]: {
      description: 'TODO',
      name: 'Space - Cluster Details',
    },
    [Id.SpaceRecordDetails]: {
      description: 'TODO',
      name: 'Space - Record Details',
    },
    [Id.SpaceRecordsList]: {
      description: 'TODO',
      name: 'Space - Records List',
    },
    [Id.SpaceServiceCenters]: {
      description: 'TODO',
      name: 'Space - Service Centers',
    },
    [Id.SpaceOrganizations]: {
      description: 'TODO',
      name: 'Space - Organizations',
    },
    [Id.SpaceSurveyConfiguration]: {
      description:
        'Manage survey configuration for the current scenario in Space Full',
      name: 'Space - Survey Configuration',
    },
    [Id.SpaceLiteSurveyConfiguration]: {
      description:
        'Manage the unlock-rooms survey tool for the current scenario in Space Lite',
      name: 'Space Lite - Survey Configuration',
    },
    [Id.SpaceUserAccess]: {
      description:
        'Manage survey admins and standard user organization assignments in Space Full',
      name: 'Space - User Access',
    },
    [Id.SpaceLiteUserAccess]: {
      description:
        'Manage survey admins and standard user organization assignments in Space Lite',
      name: 'Space Lite - User Access',
    },
    [Id.SpaceLiteRecordDetails]: {
      description: 'TODO',
      name: 'Space Lite - Record Details',
    },
    [Id.SpaceLiteRecordsList]: {
      description: 'TODO',
      name: 'Space Lite - Records List',
    },
    [Id.SpaceReports]: {
      description: 'Presents various reports used during a space survey',
      name: 'Space - Reports List',
    },
    [Id.SpaceReport]: {
      description: 'TODO',
      name: 'Space - Report Details',
    },
    [Id.SummitAllocation]: {
      description:
        'Visualizes the allocation of your team against your strategic goals',
      name: 'Summit - Allocation',
    },
    [Id.SummitCheckIns]: {
      description: 'View & manage your daily check-ins',
      name: 'Summit - Check-Ins',
    },
    [Id.SummitOrganization]: {
      description:
        'Used to create and refine organizational charts that align with your strategic vision',
      name: 'Summit - Organization',
    },
    [Id.SummitArchive]: {
      description: 'Archive for organizing and storing important notes',
      name: 'Summit - Archive',
    },
    [Id.SummitPulse]: {
      description: 'Get a sense of how your team is doing',
      name: 'Summit - Pulse',
    },
    [Id.StmChat]: {
      description: 'TODO',
      name: 'STM - Chat',
    },
    [Id.DolomiteChat]: {
      description: 'Chat with the Dolomite agent',
      name: 'Dolomite - Chat',
    },
    [Id.DolomiteGeneralChat]: {
      description: 'Chat with the Dolomite General agent',
      name: 'Dolomite General - Chat',
    },
  }

  /**
   * Returns the requested {@link Page}
   */
  export function get(id: Id): Page {
    return { ...LOOKUP[id], id }
  }

  let _list: Page[] | undefined
  /**
   * Returns all {@link Page}
   */
  export function list(): readonly Page[] {
    if (_list) {
      // If we've already created the list before, just return it
      return _list
    }
    // Otherwise, extract the list from the lookup
    _list = Object.entries(LOOKUP).map<Page>(([id, pageWithoutId]) => ({
      ...pageWithoutId,
      id: id as keyof typeof LOOKUP,
    }))
    // Sort the entries by name (ensures a consistent sorting that is also
    // user-friendly)
    _list.sort((a, b) => a.name.localeCompare(b.name))

    return _list
  }

  /**
   * There are a variety of cases where we want to associate some items with
   * certain {@link Page}, and then we want to retrieve the items
   * associated with given {@link Page}. This class can be used for
   * that use case
   *
   * @see {@link UniqueItemsLookup.listAllFor}
   * @example
   * const TABLES_LOOKUP = new UniqueItemsLookup({
   *  [Page.Id.Example1]: [ TABLE_1, TABLE_2 ]
   *  [Page.Id.Example2]: [ TABLE_2, TABLE_3 ]
   *  [Page.Id.Example3]: [ TABLE_1 ]
   *  [Page.Id.Example4]: [ TABLE_4, TABLE_5, TABLE_6 ]
   * })
   * ...
   * const prebuiltPageIdsForCurrentApp = [ Page.Id.Example1, Page.Id.Example2 ]
   * const tablesForCurrentApp = TABLES_LOOKUP.listAllFor(...prebuiltPageIdsForCurrentApp)
   * // === [ TABLE_1, TABLE_2, TABLE_3 ]
   */
  export class UniqueItemsLookup<
    const TRawLookup extends Readonly<Partial<Record<Id, readonly any[]>>>,
  > {
    constructor(public readonly raw: Readonly<TRawLookup>) {}

    /**
     * Returns a list, containing no duplicates, of the items from this lookup
     * that are associated with any of the given {@link Page.Id}
     *
     * @see {@link UniqueItemsLookup} for example
     */
    public listAllFor<TPageIds extends readonly Id[]>(
      ...prebuiltPageIds: TPageIds
    ): FlattenUnion.OfList<
      Extract<
        IndexUnion<
          TRawLookup,
          Extract<Key.In.Union<TRawLookup>, TPageIds[number]>
        >,
        readonly any[]
      >
    > {
      return uniq(prebuiltPageIds.flatMap(id => this.raw[id] ?? []))
    }
  }
}
